<template>
    <div class="sports__grid finder__app">

        <p
            role="status"
            aria-live="polite"
            class="screen-reader-text"
        >
            {{ liveRegionMessage }}
        </p>

        <div class="finder__filters">

            <h2 class="finder__label step--1" id="finder-mode-label">
                {{ printString( 'finder_browse_mode_label' ) }}
            </h2>

            <div class="finder__mode-description">
                <p v-if="browseText">{{ browseText }}</p>
            </div>

            <FinderSelectGroup
                class="finder__mode"
                v-model="mode"
                :options="modeOptions"
                :aria-label="printString( 'finder_browse_mode_label' )"
                labelled-by="finder-mode-label"
                select-id="finder-mode-select"
                dropdown-class="finder__mode-dropdown"
                @update:modelValue="onModeChange"
            />

            <h2 class="finder__label step--1" id="finder-facet-label">
                {{ facetLabel }}
            </h2>

            <FinderSelectGroup
                class="finder__facets"
                v-model="selectedFacetId"
                :options="facetOptions"
                :aria-label="facetLabel"
                labelled-by="finder-facet-label"
                select-id="finder-facet-select"
                dropdown-class="finder__facet-dropdown"
                include-empty-option
                @update:modelValue="onFacetChange"
            />

            <template v-if="hasSubImpairments">

                <h2 class="finder__label step--1" id="finder-sub-label">
                    {{ printString( 'finder_select_sub_impairment' ) }}
                </h2>

                <FinderSelectGroup
                    class="finder__subs"
                    v-model="selectedSubSlug"
                    :options="subOptions"
                    :aria-label="printString( 'finder_select_sub_impairment' )"
                    labelled-by="finder-sub-label"
                    select-id="finder-sub-select"
                    dropdown-class="finder__sub-dropdown"
                    @update:modelValue="onSubChange"
                />

            </template>

        </div>

        <div class="sports__holder">

            <h2
                v-if="!selectedFacet"
                class="finder__prompt step--1"
            >
                {{ printString( 'finder_make_selection' ) }}
            </h2>

            <div v-else class="finder__results">

                <div
                    ref="resultsSection"
                    id="finder-results"
                    class="finder__results-header"
                >
                    <h3 class="step--3">
                        {{ resultsHeading }}
                    </h3>
                    <p class="paragraph--teaser">
                        {{ resultsMatchText }}
                    </p>
                </div>

                <ul
                    v-if="resultData.eligible.length"
                    class="grid three finder__cards finder__cards--eligible"
                >
                    <template v-if="mode === 'impairment'">
                        <SportCard
                            v-for="sport in resultData.eligible"
                            :key="`eligible-sport-${sport.id}`"
                            :sport="sport"
                            :eligible="true"
                            :facet-title="facetDisplayTitle"
                            :footnote="sportFootnote( sport )"
                            :for-label="printString( 'finder_for' )"
                            :eligible-label="printString( 'finder_eligible' )"
                            :not-eligible-label="printString( 'finder_not_eligible' )"
                            :ico-check="icoCheck"
                        />
                    </template>
                    <template v-else>
                        <ImpairmentCard
                            v-for="impairment in resultData.eligible"
                            :key="`eligible-impairment-${impairment.id}`"
                            :impairment="impairment"
                            :eligible="true"
                            :facet-title="facetDisplayTitle"
                            :footnote="impairmentFootnote( impairment )"
                            :sub-titles="matchedSubTitles( impairment )"
                            :applies-to-label="printString( 'finder_applies_to' )"
                            :for-label="printString( 'finder_for' )"
                            :eligible-label="printString( 'finder_eligible' )"
                            :not-eligible-label="printString( 'finder_not_eligible' )"
                            :ico-check="icoCheck"
                            />
                    </template>
                </ul>

                <div v-if="resultData.notEligible.length">
                    <button
                        type="button"
                        class="general-button grey-border finder__show-all-toggle"
                        :aria-expanded="showAll ? 'true' : 'false'"
                        :aria-controls="showAllPanelId"
                        @click="toggleShowAll"
                    >
                        {{ showAllLabel }}
                        <span
                            class="finder__show-all-icon"
                            :class="{ 'finder__show-all-icon--open': showAll }"
                            aria-hidden="true"
                            v-html="icoChevDown"
                        ></span>
                    </button>

                    <ul
                        v-show="showAll"
                        :id="showAllPanelId"
                        class="grid three finder__cards finder__cards--all"
                    >
                        <template v-if="mode === 'impairment'">
                            <SportCard
                                v-for="entry in allItems"
                                :key="`all-sport-${entry.item.id}`"
                                :sport="entry.item"
                                :eligible="entry.eligible"
                                :facet-title="facetDisplayTitle"
                                :footnote="sportFootnote( entry.item )"
                                :for-label="printString( 'finder_for' )"
                                :eligible-label="printString( 'finder_eligible' )"
                                :not-eligible-label="printString( 'finder_not_eligible' )"
                                :ico-check="icoCheck"
                            />
                        </template>
                        <template v-else>
                            <ImpairmentCard
                                v-for="entry in allItems"
                                :key="`all-impairment-${entry.item.id}`"
                                :impairment="entry.item"
                                :eligible="entry.eligible"
                                :facet-title="facetDisplayTitle"
                                :footnote="impairmentFootnote( entry.item )"
                                :sub-titles="entry.eligible ? matchedSubTitles( entry.item ) : []"
                                :applies-to-label="printString( 'finder_applies_to' )"
                                :for-label="printString( 'finder_for' )"
                                :eligible-label="printString( 'finder_eligible' )"
                                :not-eligible-label="printString( 'finder_not_eligible' )"
                                :ico-check="icoCheck"
                            />
                        </template>
                    </ul>
                </div>

            </div>

        </div>

    </div>
</template>

<script>
import FinderSelectGroup from './FinderSelectGroup.vue';
import SportCard from './SportCard.vue';
import ImpairmentCard from './ImpairmentCard.vue';

const ALL_SUBS = '__all';

export default {
    components: {
        FinderSelectGroup,
        SportCard,
        ImpairmentCard,
    },
    data() {
        return {
            impairments: window.php_data.impairments || [],
            sports: window.php_data.sports || [],
            mode: window.php_data.initial_state?.mode || 'impairment',
            browseText: window.php_data.browse_text || '',
            selectedFacetId: window.php_data.initial_state?.facet_id || null,
            selectedSubSlug: window.php_data.initial_state?.sub || ALL_SUBS,
            showAll: false,
            liveRegionMessage: window.translations.finder_make_selection || '',
            showAllPanelId: 'finder-show-all-panel',
            icoChevDown: window.ico_chev_down || '',
            icoCheck: window.php_data.ico_check || '',
        };
    },
    computed: {
        modeOptions() {
            return [
                {
                    value: 'impairment',
                    label: this.printString( 'finder_browse_by_impairment' ),
                },
                {
                    value: 'sport',
                    label: this.printString( 'finder_browse_by_sport' ),
                },
            ];
        },
        facetOptions() {
            const items = this.mode === 'impairment' ? this.impairments : this.sports;

            return items.map( ( item ) => ( {
                value: item.id,
                label: item.title,
            } ) );
        },
        facetLabel() {
            return this.mode === 'sport'
                ? this.printString( 'finder_select_sport' )
                : this.printString( 'finder_select_impairment' );
        },
        selectedFacet() {
            if ( ! this.selectedFacetId ) {
                return null;
            }

            const items = this.mode === 'impairment' ? this.impairments : this.sports;

            return items.find( ( item ) => item.id === this.selectedFacetId ) || null;
        },
        subImpairments() {
            if ( this.mode !== 'impairment' || ! this.selectedFacet ) {
                return [];
            }

            return this.selectedFacet.sub_impairments || [];
        },
        hasSubImpairments() {
            return this.subImpairments.length > 0;
        },
        subOptions() {
            const options = this.subImpairments.map( ( sub ) => ( {
                value: sub.slug,
                label: sub.title,
            } ) );

            return [
                {
                    value: ALL_SUBS,
                    label: this.printString( 'finder_all_sub_impairments' ),
                },
                ...options,
            ];
        },
        selectedSub() {
            if ( this.selectedSubSlug === ALL_SUBS ) {
                return null;
            }

            return this.subImpairments.find( ( sub ) => sub.slug === this.selectedSubSlug ) || null;
        },
        activeSportIds() {
            if ( ! this.selectedFacet ) {
                return [];
            }

            if ( this.selectedSub ) {
                return this.selectedSub.eligible_sport_ids || [];
            }

            return this.selectedFacet.eligible_sport_ids || [];
        },
        facetDisplayTitle() {
            if ( ! this.selectedFacet ) {
                return '';
            }

            return this.selectedSub
                ? `${ this.selectedFacet.title } (${ this.selectedSub.title })`
                : this.selectedFacet.title;
        },
        resultData() {
            if ( ! this.selectedFacet ) {
                return {
                    eligible: [],
                    notEligible: [],
                };
            }

            if ( this.mode === 'impairment' ) {
                const eligibleIds = this.activeSportIds;

                return {
                    eligible: this.sports.filter( ( sport ) => eligibleIds.includes( sport.id ) ),
                    notEligible: this.sports.filter( ( sport ) => ! eligibleIds.includes( sport.id ) ),
                };
            }

            const sportId = this.selectedFacet.id;

            return {
                eligible: this.impairments.filter( ( impairment ) => ( impairment.eligible_sport_ids || [] ).includes( sportId ) ),
                notEligible: this.impairments.filter( ( impairment ) => ! ( impairment.eligible_sport_ids || [] ).includes( sportId ) ),
            };
        },
        entitySingular() {
            return this.mode === 'impairment'
                ? this.printString( 'finder_sport' )
                : this.printString( 'finder_impairment' );
        },
        entityPlural() {
            return this.mode === 'impairment'
                ? this.printString( 'finder_sports' )
                : this.printString( 'finder_impairments' );
        },
        eligibleCount() {
            return this.resultData.eligible.length;
        },
        totalCount() {
            return this.resultData.eligible.length + this.resultData.notEligible.length;
        },
        allItems() {
            if ( ! this.selectedFacet ) {
                return [];
            }

            if ( this.mode === 'impairment' ) {
                const eligibleIds = this.activeSportIds;

                return this.sports.map( ( sport ) => ( {
                    item: sport,
                    eligible: eligibleIds.includes( sport.id ),
                } ) );
            }

            const sportId = this.selectedFacet.id;

            return this.impairments.map( ( impairment ) => ( {
                item: impairment,
                eligible: ( impairment.eligible_sport_ids || [] ).includes( sportId ),
            } ) );
        },
        resultsHeading() {
            const entity = this.eligibleCount === 1 ? this.entitySingular : this.entityPlural;

            return this.buildEligibleFor( this.eligibleCount, entity, this.facetDisplayTitle );
        },
        resultsMatchText() {
            const entity = this.totalCount === 1 ? this.entitySingular : this.entityPlural;

            return this.buildMatch( this.eligibleCount, this.totalCount, entity );
        },
        showAllLabel() {
            return this.mode === 'impairment'
                ? this.printString( 'finder_show_all_sports' )
                : this.printString( 'finder_show_all_impairments' );
        },
    },
    mounted() {
        this.validateInitialFacet();
        this.validateInitialSub();
        this.updateLiveRegion( false );
        this.updateUrl();
    },
    methods: {
        printString( key ) {
            return window.translations[key] || '';
        },
        sportFootnote( sport ) {
            if ( ! sport.footnote || this.mode !== 'impairment' || ! this.selectedFacet ) {
                return '';
            }

            return ( sport.footnote_impairment_ids || [] ).includes( this.selectedFacet.id )
                ? sport.footnote
                : '';
        },
        matchedSubTitles( impairment ) {
            if ( this.mode !== 'sport' || ! this.selectedFacet ) {
                return [];
            }

            const subs = impairment.sub_impairments || [];

            return subs
                .filter( ( sub ) => ( sub.eligible_sport_ids || [] ).includes( this.selectedFacet.id ) )
                .map( ( sub ) => sub.title );
        },
        impairmentFootnote( impairment ) {
            if ( this.mode !== 'sport' || ! this.selectedFacet || ! this.selectedFacet.footnote ) {
                return '';
            }

            return ( this.selectedFacet.footnote_impairment_ids || [] ).includes( impairment.id )
                ? this.selectedFacet.footnote
                : '';
        },
        buildEligibleFor( count, entity, facet ) {
            return this.printString( 'finder_eligible_for' )
                .replace( '%1$s', count )
                .replace( '%2$s', entity )
                .replace( '%3$s', facet );
        },
        buildMatch( count, total, entity ) {
            return [
                count,
                this.printString( 'finder_of' ),
                total,
                entity,
                `${ this.printString( 'finder_match' ) }.`,
            ].join( ' ' );
        },
        validateInitialFacet() {
            if ( ! this.selectedFacetId ) {
                return;
            }

            const isValid = this.facetOptions.some( ( option ) => option.value === this.selectedFacetId );

            if ( ! isValid ) {
                this.selectedFacetId = null;
            }
        },
        validateInitialSub() {
            if ( this.selectedSubSlug === ALL_SUBS ) {
                return;
            }

            if ( ! this.selectedSub ) {
                this.selectedSubSlug = ALL_SUBS;
            }
        },
        onModeChange() {
            this.selectedFacetId = null;
            this.selectedSubSlug = ALL_SUBS;
            this.showAll = false;
            this.updateLiveRegion( false );
            this.updateUrl();
        },
        onFacetChange() {
            this.selectedSubSlug = ALL_SUBS;
            this.showAll = false;
            this.updateLiveRegion( true );
            this.updateUrl();
            this.scrollToResults();
        },
        onSubChange() {
            this.showAll = false;
            this.updateLiveRegion( true );
            this.updateUrl();
            this.scrollToResults();
        },
        scrollToResults() {
            if ( ! this.selectedFacet ) {
                return;
            }

            this.$nextTick( () => {
                const resultsEl = this.$refs.resultsSection;

                if ( ! resultsEl ) {
                    return;
                }

                const rect = resultsEl.getBoundingClientRect();
                const peekHeight = 300;
                const revealOffset = window.innerHeight - peekHeight;

                if ( rect.top <= revealOffset ) {
                    return;
                }

                const behavior = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
                    ? 'auto'
                    : 'smooth';

                window.scrollTo( {
                    top: window.scrollY + rect.top - revealOffset,
                    behavior,
                } );
            } );
        },
        toggleShowAll() {
            this.showAll = ! this.showAll;
        },
        updateLiveRegion( announceResults ) {
            if ( ! announceResults || ! this.selectedFacet ) {
                this.liveRegionMessage = this.printString( 'finder_make_selection' );
                return;
            }

            const entity = this.eligibleCount === 1 ? this.entitySingular : this.entityPlural;

            this.liveRegionMessage = [
                this.printString( 'finder_results_updated' ),
                this.buildEligibleFor( this.eligibleCount, entity, this.facetDisplayTitle ),
            ].join( ' ' );
        },
        updateUrl() {
            const params = new URLSearchParams();
            params.set( 'mode', this.mode );

            if ( this.selectedFacet ) {
                params.set( 'facet', this.selectedFacet.slug );
            }

            if ( this.selectedSub ) {
                params.set( 'sub', this.selectedSub.slug );
            }

            const query = params.toString();
            const nextUrl = query
                ? `${window.location.pathname}?${query}`
                : window.location.pathname;

            window.history.replaceState( {}, '', nextUrl );
        },
    },
};
</script>
